import { TLShape, TLShapeId, useEditor, useValue } from "@tldraw/tldraw";
import React, { useState, useEffect } from "react";
import * as RadixContextMenu from "@radix-ui/react-context-menu";
import { DropdownChevronIcon } from "pages/flyer/builder/drawer/components/Primitives/icons/DropdownChevronIcon";
import { Tooltip } from "pages/flyer/builder/drawer/components/Primitives/Tooltip";
import { LayersContextMenuContent } from "./LayersContextMenu";
import { useTldrawApp } from "pages/flyer/builder/drawer/hooks";
import MapPinIcon from "components/icons/components/MapPinIcon";
import { getShapeName } from "pages/flyer/builder/utils";
import { enterMaskEditMode } from "pages/flyer/builder/drawer/utils/booleanOperations";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import cn from "classnames";
import { ShapeList } from "./ShapeListComponent";
import { TextIcon, ImageIcon } from "../RenderIcon/iconsv2";
import GroupIcon from "../RenderIcon/icons/GroupIcon";
import {
  EyeOpenIcon16,
  EyeClosedIcon16,
  LockUnlockedIcon,
  LockLockedIcon,
  QRLayerIcon,
  IconsLayerIcon,
  ShapesLayerIcon,
} from "./icons";
import "./style.css";

const ICON_SIZE = { width: 16, height: 16 };

// Geometric shape types that should all use the generic Shapes icon
const GEOMETRIC_SHAPE_TYPES = new Set([
  "customRect",
  "rectangle",
  "customEllipse",
  "ellipse",
  "customTriangle",
  "triangle",
  "customStar",
  "star",
  "customHeart",
  "heart",
  "customDiamond",
  "diamond",
  "customHexagon",
  "hexagon",
  "customPentagon",
  "pentagon",
  "customOctagon",
  "octagon",
  "customCloud",
  "cloud",
  "customRhombus",
  "rhombus",
  "customTrapezoid",
  "trapezoid",
  "customXBox",
  "xBox",
  "customArrowUp",
  "customArrowDown",
  "customArrowLeft",
  "customArrowRight",
  "arrowUp",
  "arrowDown",
  "arrowLeft",
  "arrowRight",
  "draw",
]);

export function getShapeIcon(shape: any) {
  if (!shape) return null;
  const type = shape.type;

  // QR code detection
  const assetId = shape.props?.assetId ? String(shape.props.assetId) : "";
  const isQR =
    String(shape.id).includes("qrCode") ||
    (type === "customImage" && shape.props?.qrCodeLink) ||
    (type === "customImage" && assetId.toLowerCase().includes("qr"));
  if (isQR) return <QRLayerIcon />;

  // Geometric shapes → generic Shapes icon
  if (GEOMETRIC_SHAPE_TYPES.has(type)) return <ShapesLayerIcon />;

  switch (type) {
    case "customImage":
    case "image":
      return <ImageIcon {...ICON_SIZE} />;
    case "customText":
    case "text":
    case "customLabel":
      return <TextIcon {...ICON_SIZE} />;
    case "customSvg":
      return <IconsLayerIcon />;
    case "group":
      return <GroupIcon />;
    case "customAddressBox":
      return <MapPinIcon size={16} />;
    default:
      return <ShapesLayerIcon />;
  }
}

interface ShapeItemProps {
  shapeId: TLShapeId;
  depth: number;
  parentIsSelected?: boolean;
  parentIsHidden?: boolean;
  index: number;
  shape?: any;
  onClick?: (shiftKey?: boolean) => void;
}

function ShapeItem({ shapeId, depth, parentIsSelected, parentIsHidden, index, onClick }: ShapeItemProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [zIndex, setZIndex] = useState(0);
  const mouseDownPos = React.useRef<{ x: number; y: number } | null>(null);

  const editor = useEditor();
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: shapeId,
  });
  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: shapeId,
    data: { depth, index },
    disabled: isEditingName,
  });

  const shape = useValue("shape", () => editor.getShape(shapeId), [editor]);
  const children = useValue("children", () => editor.getSortedChildIdsForParent(shapeId), [editor]);
  const isHidden = useValue("isHidden", () => editor.isShapeHidden(shapeId), [editor]);
  const isSelected = useValue("isSelected", () => editor.getSelectedShapeIds().includes(shapeId), [editor]);
  const shapeName = useValue("shapeName", () => getShapeName(editor, shapeId, index), [editor]);
  const hoveredShape = useValue("hoveredShape", () => editor.getHoveredShape(), [editor]);

  // useEffect(() => {
  //   if (shape?.type === "group" && (!shape.meta || !shape.meta.name)) {
  //     const groupName = getShapeName(editor, shapeId, index);
  //     editor.updateShape({
  //       ...shape,
  //       meta: {
  //         ...(shape.meta && typeof shape.meta === "object" ? shape.meta : {}),
  //         name: groupName || "Unnamed Group",
  //       },
  //     });
  //   }
  // }, [shape, editor, shapeId, index]);

  const selectedBg = "#E2E2FC";
  const childSelectedBg = "white";
  const childBg = "white";
  const style = {
    transform: CSS.Translate.toString(transform),
    zIndex: zIndex,
    // Section header layout: [chevron][gap 8px][icon][gap 8px][text]
    // Non-group depth 0: paddingLeft 21px → icon aligns with section icon
    // Group depth 0: paddingLeft 25px → chevron aligns with section chevron
    // Group children (depth 1+): offset + depth*20px
    paddingLeft: (children?.length > 0 ? 25 : 21) + depth * 20,
    opacity: parentIsHidden || isHidden ? 0.5 : 1,
    // @ts-ignore
    borderBottom: isOver && editor.inputs.dragOverPosition === "below" ? "2px solid rgb(253 164 175)" : undefined,
    // @ts-ignore
    borderTop: isOver && editor.inputs.dragOverPosition === "above" ? "2px solid rgb(253 164 175)" : undefined,
    background: isSelected ? selectedBg : parentIsSelected ? childSelectedBg : depth > 0 ? childBg : undefined,
  };

  const childrenShapes = useValue(
    "shapes",
    () =>
      children.map((shapeId) => {
        const shape = editor.getShape(shapeId);
        return shape;
      }),
    [editor, children]
  );

  // const handleClickItem = (shape: any) => {
  //   if (isDragging) return;
  //   if (shape.type === "group") {
  //     setIsExpanded(!isExpanded);
  //   } else {
  //     if (editor.inputs.ctrlKey || editor.inputs.shiftKey) {
  //       if (isSelected) {
  //         editor.deselect(shape);
  //       } else {
  //         editor.select(...editor.getSelectedShapes(), shape);
  //       }
  //     } else {
  //       editor.select(shape);
  //     }
  //   }
  // };

  const handleMouseDown = (event: React.MouseEvent) => {
    if (event.button !== 0) return;
    // Dismiss any stuck Radix context menu from a previous right-click.
    // Radix doesn't detect outside-clicks that are swallowed by tldraw,
    // so pressing Escape forces it closed.
    document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
    setIsDragging(false);
    mouseDownPos.current = { x: event.clientX, y: event.clientY };
    setZIndex(10000);
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (event.button !== 0 || !mouseDownPos.current) return;
    const dx = event.clientX - mouseDownPos.current.x;
    const dy = event.clientY - mouseDownPos.current.y;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
      setIsDragging(true);
    }
  };

  const handleMouseUp = (event: React.MouseEvent) => {
    if (event.button !== 0) return;
    // Locked shapes cannot be selected from layers panel
    if (!isDragging && onClick && !shape?.isLocked) {
      // Pass the native shiftKey state since editor.inputs.shiftKey
      // may not track keyboard state when pointer is outside the canvas
      onClick(event.shiftKey);
    }
    setIsDragging(false);
    mouseDownPos.current = null;
    setZIndex(0);
  };

  const handleHiddenToggle = (ev: React.MouseEvent) => {
    editor.run(
      () => {
        //@ts-ignore
        editor.updateShape({ ...shape, meta: { hidden: !shape.meta.hidden } });
      },
      { ignoreShapeLock: true }
    );
    ev.stopPropagation();
    setIsExpanded(true);
  };

  const handleLockToggle = (ev: React.MouseEvent) => {
    if (!shape) return;
    const willLock = !shape.isLocked;
    editor.run(
      () => {
        editor.updateShape({ id: shape.id, type: shape.type, isLocked: willLock });
      },
      { ignoreShapeLock: true }
    );
    // Deselect shape when locking it
    if (willLock && editor.getSelectedShapeIds().includes(shape.id)) {
      editor.deselect(shape);
    }
    ev.stopPropagation();
  };

  const app = useTldrawApp();

  // Select shape on right-click so the parent <ContextMenu> (which wraps the
  // entire tldraw canvas area) sees the selection and shows the correct menu.
  // No Radix wrapper on this layer item — the event bubbles to the parent.
  const handleContextMenu = React.useCallback(() => {
    if (!shape) return;
    // Ensure tldraw is in select tool
    if (!editor.isIn("select")) {
      editor.setCurrentTool("select");
    }
    // If this shape is already part of a multi-selection, preserve it.
    // Only replace selection if the shape isn't currently selected.
    const currentIds = editor.getSelectedShapeIds();
    if (!currentIds.includes(shapeId)) {
      editor.select(shape);
      app.setSelectedIds([shapeId]);
    } else {
      // Shape is already selected (possibly as part of multi-select) — just sync v1
      app.setSelectedIds([...currentIds]);
    }
    // tldraw's pointerdown handler may also fire and select a canvas shape underneath.
    // Override by re-selecting after tldraw processes (next microtask).
    const preservedIds = [...editor.getSelectedShapeIds()];
    setTimeout(() => {
      const afterIds = editor.getSelectedShapeIds();
      if (JSON.stringify(afterIds) !== JSON.stringify(preservedIds)) {
        editor.setSelectedShapes(preservedIds.map((id: any) => editor.getShape(id)).filter(Boolean) as TLShape[]);
        app.setSelectedIds([...preservedIds]);
      }
    }, 0);
  }, [shape, shapeId, app, editor]);

  return (
    <>
      {!!shape && (
        <RadixContextMenu.Root dir="ltr">
          <RadixContextMenu.Trigger asChild onContextMenu={handleContextMenu}>
            <div
              style={{ ...style }}
              className={cn(
                shapeId === "shape:customMeasure" ? "hidden" : "",
                shapeId === hoveredShape?.id ? "!bg-[#E2E2FC] text-[#1a2547]" : "",
                !isSelected && depth === 0 ? "hover:text-[#1a2547]" : "hover:!text-[#1a2547]",
                "group/item relative hover:bg-[#E2E2FC] rounded-[4px]"
              )}
              key={shapeId}
              ref={(node) => {
                setNodeRef(node);
                setDroppableRef(node);
              }}
              id={`shape-item-${shapeId}`}
              onMouseEnter={() => {
                editor.setHoveredShape(shape.id);
              }}
              onMouseLeave={() => {
                editor.setHoveredShape(null);
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={(event) => handleMouseUp(event)}
              onDoubleClick={() => {
                // if is mask shape, enter edit mode (same as on canvas)
                const isMaskShape = shape?.meta?.isMaskShape;
                const isCompositePart = shape?.meta?.isCompositePart;

                if (isMaskShape || isCompositePart) {
                  let maskShapeId: TLShapeId = shape.id;

                  if (isCompositePart && !isMaskShape) {
                    const parentId = shape.parentId;
                    const parentGroup = parentId ? editor.getShape(parentId) : null;
                    if (!parentGroup?.meta?.isCompositeResult) return;
                    maskShapeId = parentId as TLShapeId;
                  }

                  enterMaskEditMode(maskShapeId, editor, { selectGroup: true });
                  return;
                }

                setIsEditingName(true);
              }}
            >
              <div
                className={cn(
                  "shape-item w-[210px] h-7 flex !items-center",
                  shape?.isLocked ? "cursor-not-allowed opacity-60" : "cursor-pointer",
                  children?.length > 0 ? "w-[220px]" : "w-[210px]"
                )}
                {...(!editor.inputs.ctrlKey && !isEditingName && !shape?.isLocked ? listeners : {})}
                {...(!editor.inputs.ctrlKey && !isEditingName && !shape?.isLocked ? attributes : {})}
              >
                {isEditingName ? (
                  <input
                    autoFocus
                    className="shape-name-input capitalize-first"
                    defaultValue={shapeName || shape.meta.name || ""}
                    onBlur={() => setIsEditingName(false)}
                    onChange={(ev) => {
                      const newValue = ev.target.value;
                      if (shape.type === "frame") {
                        editor.updateShape({ ...shape, props: { ...shape.props, name: newValue } });
                      } else {
                        editor.updateShape({ ...shape, meta: { ...shape.meta, name: newValue, isCustomName: true } });
                      }
                    }}
                    onKeyDown={(ev) => {
                      if (ev.key === "Enter" || ev.key === "Escape") {
                        ev.currentTarget.blur();
                      }
                    }}
                  />
                ) : (
                  <div className="shape-name flex items-center w-full min-w-0 pr-16" style={{ gap: 8 }}>
                    {children?.length ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsExpanded((prev) => !prev);
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                        className="flex items-center justify-center flex-shrink-0 p-0 border-0 bg-transparent cursor-pointer"
                        style={{ width: 9 }}
                      >
                        <DropdownChevronIcon
                          style={{
                            transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                            transition: "transform 0.2s ease",
                          }}
                        />
                      </button>
                    ) : null}
                    <span className="flex-shrink-0 flex items-center justify-center" style={{ width: 16, height: 16 }}>
                      {getShapeIcon(shape)}
                    </span>
                    <Tooltip label={shapeName || shape.meta.name || "Unnamed Shape"} side="top">
                      <div
                        className="capitalize-first overflow-hidden text-ellipsis min-w-0"
                        style={{
                          color: "#0C172C",
                          fontFeatureSettings: "'liga' off, 'clig' off",
                          fontFamily: "Inter",
                          fontSize: 14,
                          fontWeight: 400,
                          lineHeight: "16px",
                          whiteSpace: "nowrap",
                          maxWidth: 70,
                        }}
                      >
                        {shapeName || shape.meta.name || "Unnamed Shape"}
                      </div>
                    </Tooltip>
                  </div>
                )}
              </div>
              <div
                className={cn(
                  "flex items-center absolute top-1/2 right-0 z-50",
                  isSelected || shape.isLocked || shape.meta.hidden ? "visible" : "invisible group-hover/item:visible"
                )}
                style={{ transform: "translateY(-50%)" }}
              >
                <button
                  className="shape-visibility-toggle"
                  onClick={(ev) => {
                    handleLockToggle(ev);
                  }}
                >
                  {shape.isLocked ? <LockLockedIcon /> : <LockUnlockedIcon />}
                </button>
                <button
                  className="shape-visibility-toggle"
                  onClick={(ev) => {
                    handleHiddenToggle(ev);
                  }}
                >
                  {shape.meta.hidden ? <EyeClosedIcon16 /> : <EyeOpenIcon16 />}
                </button>
              </div>
            </div>
          </RadixContextMenu.Trigger>
          <LayersContextMenuContent targetShapeId={shapeId} targetShapeIds={editor.getSelectedShapeIds()} />
        </RadixContextMenu.Root>
      )}
      {(children?.length ?? 0) > 0 && isExpanded && (
        <ShapeList
          shapeIds={children}
          depth={depth + 1}
          parentIsHidden={parentIsHidden}
          parentIsSelected={parentIsSelected}
          shapes={childrenShapes}
        />
      )}
    </>
  );
}

export default ShapeItem;
